import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSignIn, useClerk } from '@clerk/clerk-react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, Loader2 } from 'lucide-react';
import GoogleButton from '../components/auth/GoogleButton';
import { OTPInput } from '../components/auth/OTPInput';
import { ClerkStatusNotice } from '../components/auth/ClerkStatusNotice';
import {
  authErrorMessage, clerkErrorCode, exchangeClerkSession, linkExistingAccount,
  routeAfterLogin, startSecondFactorEmail,
} from '../lib/clerkAuth';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('password'); // 'password' | 'code'
  const [codeStep, setCodeStep] = useState(null); // null | 'first' | 'second'
  const [code, setCode] = useState('');
  const { guestLogin, checkAuth } = useAuth();
  const { isLoaded, signIn, setActive } = useSignIn();
  const clerk = useClerk();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const finish = async (result) => {
    await setActive({ session: result.createdSessionId });
    const data = await exchangeClerkSession(clerk);
    toast.success('Welcome back!');
    await routeAfterLogin(data, { navigate, checkAuth, from });
  };

  const handleResult = async (result) => {
    if (result.status === 'complete') return finish(result);
    if (result.status === 'needs_second_factor' || result.status === 'needs_client_trust') {
      await startSecondFactorEmail(signIn);
      setCodeStep('second');
      setCode('');
      toast.success('We have emailed you a 6-digit code to confirm it is you');
      return;
    }
    throw new Error('Sign-in needs another step. Please try again.');
  };

  // Accounts created before Clerk are copied across on their first login.
  const withFirstLoginLink = async (attempt, linkPassword) => {
    try {
      return await attempt();
    } catch (err) {
      if (clerkErrorCode(err) !== 'form_identifier_not_found') throw err;
      await linkExistingAccount(email.trim(), linkPassword);
      return attempt();
    }
  };

  const startEmailCode = async () => {
    const result = await withFirstLoginLink(() => signIn.create({ identifier: email.trim() }));
    const factor = result.supportedFirstFactors?.find((f) => f.strategy === 'email_code');
    if (!factor) throw new Error('Email codes are not switched on for sign-in.');
    await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: factor.emailAddressId });
    setCodeStep('first');
    setCode('');
    toast.success('Check your inbox for a 6-digit code');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoaded) return;
    setLoading(true);
    try {
      if (mode === 'code') {
        await startEmailCode();
      } else {
        const result = await withFirstLoginLink(
          () => signIn.create({ identifier: email.trim(), password }), password,
        );
        await handleResult(result);
      }
    } catch (error) {
      const c = clerkErrorCode(error);
      toast.error(c === 'form_identifier_not_found' || c === 'form_password_incorrect'
        ? 'Invalid email or password' : authErrorMessage(error, 'Sign-in failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const result = codeStep === 'first'
        ? await signIn.attemptFirstFactor({ strategy: 'email_code', code })
        : await signIn.attemptSecondFactor({ strategy: 'email_code', code });
      await handleResult(result);
    } catch (error) {
      toast.error(authErrorMessage(error, 'Invalid code'));
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setLoading(true);
    try {
      await guestLogin();
      toast.success('Welcome, Guest!');
      navigate('/discover');
    } catch (error) {
      console.error('Guest login failed:', error);
      toast.error(error?.response?.data?.detail || error?.message || 'Guest login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (codeStep) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-4">
        <div className="w-full max-w-md card-surface p-8 text-center" data-testid="login-code-step">
          <div className="w-16 h-16 rounded-full bg-[#F9F6F0] border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-4">
            <Mail className="h-7 w-7 text-[#D4AF37]" />
          </div>
          <h2 className="font-heading text-xl font-medium text-[#1A1A1A] mb-1">Check your inbox</h2>
          <p className="text-sm text-stone-500 mb-6">
            Enter the 6-digit code we sent to<br />
            <span className="font-medium text-[#1A1A1A]">{email}</span>
          </p>
          <OTPInput value={code} onChange={setCode} disabled={loading} />
          <Button onClick={handleVerifyCode} disabled={code.length !== 6 || loading}
            className="w-full btn-primary h-12 mt-6" data-testid="login-code-submit">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Checking...</> : 'Sign In'}
          </Button>
          <button onClick={() => { setCodeStep(null); setCode(''); }}
            className="mt-4 text-sm text-stone-400 hover:text-stone-600 inline-flex items-center gap-1"
            data-testid="login-code-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center bg-[#FAFAFA] overflow-y-auto">
      <div className="flex flex-col justify-center px-6 py-8 sm:py-12 lg:px-16 w-full max-w-lg pb-24 sm:pb-12">
        <div className="mx-auto w-full max-w-md">
          <Link to="/" className="block mb-8">
            <span className="font-heading text-3xl font-semibold text-[#1A1A1A]">ZURIYA</span>
          </Link>

          <h1 className="font-heading text-3xl font-medium text-[#1A1A1A] mb-2" data-testid="login-title">
            Welcome Back
          </h1>
          <p className="text-stone-500 mb-8">Sign in to continue to your account</p>
          <ClerkStatusNotice />

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1A1A1A]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input-zuriya pl-12" placeholder="Enter your email" required data-testid="login-email" />
              </div>
            </div>

            {mode === 'password' && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1A1A1A]">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)} className="input-zuriya pl-12 pr-12"
                    placeholder="Enter your password" required data-testid="login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between gap-4">
              <button type="button" onClick={() => setMode(mode === 'password' ? 'code' : 'password')}
                className="text-sm text-[#D4AF37] hover:underline" data-testid="login-mode-toggle">
                {mode === 'password' ? 'Email me a code instead' : 'Use my password instead'}
              </button>
              <Link to="/forgot-password" className="text-sm text-[#D4AF37] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <Button type="submit" className="w-full btn-primary h-12 rounded-lg" disabled={loading || !isLoaded}
              data-testid="login-submit">
              {loading ? 'Signing in...' : mode === 'code' ? 'Send me a code' : 'Sign In'}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E6E4DD]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-stone-500">or</span>
            </div>
          </div>

          <div className="space-y-3">
            <GoogleButton />
            <Button type="button" variant="outline" className="w-full h-12 rounded-lg border-[#E6E4DD] hover:bg-[#F9F6F0]"
              onClick={handleGuestLogin} disabled={loading} data-testid="guest-login">
              <User className="w-5 h-5 mr-2" />
              Continue as Guest
            </Button>
          </div>

          <p className="mt-8 text-center text-stone-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#D4AF37] hover:underline font-medium" data-testid="register-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
